import * as React from 'react';
import { cn } from '../utils/cn';

// ─── TreeNode data ──────────────────────────────────────────────────────

export interface TreeNodeData {
  value: string;
  title: string;
  children?: TreeNodeData[];
}

// ─── TreeNode JSX helper (antd-compat) ──────────────────────────────────

export interface TreeNodeProps {
  value: string;
  title: string;
  children?: React.ReactNode;
}

/** Declarative TreeNode — only used to build the tree structure via JSX */
export function TreeNode(_props: TreeNodeProps): React.ReactElement | null {
  return null; // Rendering is handled by TreeSelect
}

// ─── Internal: parse JSX children into TreeNodeData[] ───────────────────

function parseChildren(children: React.ReactNode): TreeNodeData[] {
  const nodes: TreeNodeData[] = [];
  React.Children.forEach(children, (child) => {
    if (!React.isValidElement<TreeNodeProps>(child)) return;
    const { value, title, children: nested } = child.props;
    nodes.push({
      value,
      title,
      children: nested ? parseChildren(nested) : undefined,
    });
  });
  return nodes;
}

// ─── Internal: flatten tree for search ──────────────────────────────────

function flattenTree(nodes: TreeNodeData[]): TreeNodeData[] {
  const result: TreeNodeData[] = [];
  for (const node of nodes) {
    result.push(node);
    if (node.children) {
      result.push(...flattenTree(node.children));
    }
  }
  return result;
}

// ─── Internal: filter tree by search ────────────────────────────────────

function filterTree(nodes: TreeNodeData[], query: string): TreeNodeData[] {
  const lower = query.toLowerCase();
  const result: TreeNodeData[] = [];
  for (const node of nodes) {
    const childMatches = node.children ? filterTree(node.children, query) : [];
    if (node.title.toLowerCase().includes(lower) || childMatches.length > 0) {
      result.push({
        ...node,
        children: childMatches.length > 0 ? childMatches : node.children,
      });
    }
  }
  return result;
}

// ─── Internal: recursive tree renderer ──────────────────────────────────

function TreeBranch({
  nodes,
  depth,
  selected,
  onSelect,
}: {
  nodes: TreeNodeData[];
  depth: number;
  selected: string | null;
  onSelect: (value: string) => void;
}) {
  return (
    <>
      {nodes.map((node) => (
        <React.Fragment key={node.value}>
          <div
            className={cn(
              'cursor-pointer select-none px-3 py-1.5 text-sm hover:bg-blue-50 transition-colors',
              selected === node.value && 'bg-blue-100 text-blue-700 font-medium'
            )}
            style={{ paddingLeft: `${12 + depth * 16}px` }}
            onClick={() => onSelect(node.value)}
          >
            {node.children && node.children.length > 0 && (
              <span className="inline-block w-4 text-gray-400 mr-0.5">▸</span>
            )}
            {node.title}
          </div>
          {node.children && node.children.length > 0 && (
            <TreeBranch
              nodes={node.children}
              depth={depth + 1}
              selected={selected}
              onSelect={onSelect}
            />
          )}
        </React.Fragment>
      ))}
    </>
  );
}

// ─── TreeSelect ─────────────────────────────────────────────────────────

export interface TreeSelectProps {
  value?: string | null;
  onChange?: (value: string | null) => void;
  placeholder?: string;
  allowClear?: boolean;
  showSearch?: boolean;
  treeDefaultExpandAll?: boolean;
  size?: 'large' | 'default' | 'small';
  style?: React.CSSProperties;
  dropdownStyle?: React.CSSProperties;
  className?: string;
  children?: React.ReactNode;
  /** Pre-built tree data (alternative to JSX children) */
  treeData?: TreeNodeData[];
}

const sizeStyles: Record<string, string> = {
  large: 'h-12 text-base',
  default: 'h-10 text-sm',
  small: 'h-8 text-xs',
};

function TreeSelectBase({
  value = null,
  onChange,
  placeholder = 'Välj...',
  allowClear = false,
  showSearch = false,
  size = 'default',
  style,
  dropdownStyle,
  className,
  children,
  treeData,
}: TreeSelectProps) {
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState('');
  const containerRef = React.useRef<HTMLDivElement>(null);

  // Build tree from JSX children or treeData prop
  const tree = React.useMemo(() => {
    if (treeData) return treeData;
    if (children) return parseChildren(children);
    return [];
  }, [treeData, children]);

  // Find label for current value
  const allNodes = React.useMemo(() => flattenTree(tree), [tree]);
  const selectedLabel = allNodes.find((n) => n.value === value)?.title ?? '';

  // Filter tree by search query
  const visibleTree = search ? filterTree(tree, search) : tree;

  // Close dropdown on outside click
  React.useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <div ref={containerRef} className={cn('relative', className)} style={style}>
      {/* Trigger */}
      <div
        className={cn(
          'flex items-center w-full bg-white border border-gray-300 rounded px-3 cursor-pointer',
          'hover:border-blue-400 transition-colors',
          sizeStyles[size]
        )}
        onClick={() => setOpen(!open)}
      >
        <span className={cn('flex-1 truncate', !value && 'text-gray-400')}>
          {selectedLabel || placeholder}
        </span>
        {allowClear && value && (
          <span
            className="text-gray-400 hover:text-gray-600 mr-1"
            onClick={(e) => {
              e.stopPropagation();
              onChange?.(null);
            }}
          >
            ✕
          </span>
        )}
        <svg
          className="text-gray-400 shrink-0"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </div>

      {/* Dropdown */}
      {open && (
        <div
          className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded shadow-lg"
          style={{ maxHeight: 400, overflow: 'auto', ...dropdownStyle }}
        >
          {showSearch && (
            <div className="p-2 border-b border-gray-100">
              <input
                type="text"
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-400"
                placeholder="Sök..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                autoFocus
              />
            </div>
          )}
          {visibleTree.length === 0 ? (
            <div className="px-3 py-2 text-sm text-gray-400">Inga träffar</div>
          ) : (
            <TreeBranch
              nodes={visibleTree}
              depth={0}
              selected={value}
              onSelect={(val) => {
                onChange?.(val);
                setOpen(false);
                setSearch('');
              }}
            />
          )}
        </div>
      )}
    </div>
  );
}

// Attach TreeNode for antd compat: TreeSelect.TreeNode
export const TreeSelect = Object.assign(TreeSelectBase, { TreeNode });
